// src/components/chat/ChatBubble.js
import React, { useState, useEffect, useCallback } from 'react';
import { X } from 'lucide-react';

const formatTime = (timestamp) => {
  if (!timestamp) return '';
  const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
  return date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
};

const ImageLightbox = ({ src, onClose }) => {
  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === 'Escape') onClose();
    },
    [onClose]
  );

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center bg-black/90 animate-fade-in"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="Image en plein écran"
    >
      <button
        onClick={onClose}
        aria-label="Fermer"
        className="absolute top-4 right-4 p-2 text-white/80 hover:text-white rounded-full hover:bg-white/10 transition-colors"
      >
        <X size={24} />
      </button>
      <img
        src={src}
        alt="Contenu partagé"
        className="max-w-full max-h-full object-contain p-4"
        onClick={(e) => e.stopPropagation()}
      />
    </div>
  );
};

const ChatBubble = ({ message, isMine, showTimestamp, otherReadAt }) => {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const hasImage = !!message.imageUrl;
  const hasText = !!message.text;
  const imageOnly = hasImage && !hasText;

  // Read receipt : comparer createdAt du message avec otherReadAt
  const isRead = (() => {
    if (!isMine || !otherReadAt || !message.createdAt) return false;
    const msgTime = message.createdAt?.toMillis?.() || 0;
    const readTime = otherReadAt?.toMillis?.() || 0;
    return readTime >= msgTime;
  })();

  return (
    <>
      <div className={`flex ${isMine ? 'justify-end' : 'justify-start'} animate-fade-in-up`}>
        <div
          data-testid="chat-bubble"
          className={`max-w-[75%] rounded-2xl ${imageOnly ? 'p-1.5' : 'px-3 py-2 sm:px-4 sm:py-2.5'} ${
            isMine
              ? 'bg-gradient-to-br from-rose-500 to-fuchsia-600 text-white rounded-br-md'
              : 'bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-gray-100 rounded-bl-md'
          }`}
        >
          {hasImage && (
            <img
              src={message.imageUrl}
              alt="Contenu partagé"
              style={{ maxWidth: 240 }}
              className="rounded-xl object-cover cursor-pointer hover:opacity-90 transition-opacity"
              onClick={() => setLightboxOpen(true)}
            />
          )}
          {hasText && (
            <p
              className={`text-sm whitespace-pre-wrap break-words ${hasImage ? 'px-2.5 pt-1.5' : ''}`}
            >
              {message.text}
            </p>
          )}
          <div
            className={`flex items-center gap-1 ${hasImage && !hasText ? 'px-1.5' : ''} ${
              isMine ? 'justify-end' : ''
            }`}
          >
            {showTimestamp && (
              <p
                className={`text-[10px] mt-1 ${
                  isMine ? 'text-white/60' : 'text-gray-400 dark:text-gray-500'
                }`}
              >
                {formatTime(message.createdAt)}
              </p>
            )}
            {isMine && (
              <span
                className={`text-[10px] mt-1 ${isRead ? 'text-sky-300' : 'text-white/50'}`}
                aria-label={isRead ? 'Lu' : 'Envoyé'}
              >
                {isRead ? '✓✓' : '✓'}
              </span>
            )}
          </div>
        </div>
      </div>
      {lightboxOpen && (
        <ImageLightbox src={message.imageUrl} onClose={() => setLightboxOpen(false)} />
      )}
    </>
  );
};

export default ChatBubble;
